import { ISnListResponse } from '../interfaces/list-response.interface';

/**
 * Extracts cursor value from DRF pagination URL.
 *
 * Django REST Framework cursor pagination returns full or relative URLs
 * in the `next` and `previous` fields. This utility extracts just the
 * cursor parameter value for use in subsequent requests.
 *
 * @param url - Full or relative URL from next/previous response field
 * @returns cursor string or null if not present
 *
 * @example
 * extractCursor('/api/cameras/?cursor=cD0yMDI1&limit=20')
 * // Returns: 'cD0yMDI1'
 *
 * @example
 * extractCursor('http://api.example.com/cameras/?cursor=abc123&ordering=-created')
 * // Returns: 'abc123'
 *
 * @example
 * extractCursor(null)
 * // Returns: null
 */
export function extractCursor(url: string | null): string | null {
  if (!url) {
    return null;
  }

  try {
    // Try parsing as full URL first
    const urlObj = new URL(url, window.location.origin);
    return urlObj.searchParams.get('cursor');
  } catch {
    // Fallback for relative URLs - use regex
    const match = url.match(/[?&]cursor=([^&]+)/);
    return match ? decodeURIComponent(match[1]) : null;
  }
}

/**
 * Transforms a paginated API response into a standardized ISnListResponse format.
 * Supports both page-based and cursor-based pagination automatically.
 *
 * Accepts raw API response with plain objects and transforms results using the provided mapping function.
 *
 * @template Model - The transformed entity type
 * @param response - Raw API response (results contain plain objects)
 * @param mapFn - Function to transform each result item into a domain model
 * @param additionalFields - Optional additional fields to merge into the response
 * @returns Transformed list response with entity instances
 *
 * @example
 * return this._httpClient.get('/api/cameras/', { params }).pipe(
 *   map((response: any) =>
 *     transformListResponse(response, (item) => new SnCamera(item))
 *   )
 * );
 *
 * @example
 * // With additional fields:
 * return this._httpClient.get('/api/locations/', { params }).pipe(
 *   map((response: any) =>
 *     transformListResponse(
 *       response,
 *       (item) => new SnLocation(item),
 *       { ordering: response.ordering }
 *     )
 *   )
 * );
 */
export function transformListResponse<Model>(
  response: ISnListResponse<any>,
  mapFn: (item: any) => Model,
  additionalFields?: Record<string, any>,
): ISnListResponse<Model> {
  return {
    // Results
    results: response.results.map(mapFn),
    // Page-based pagination
    count: response.count,
    totalPages: response.totalPages,
    // Cursor-based pagination
    next: response.next,
    previous: response.previous,
    // Any additional fields
    ...additionalFields,
  };
}
